import * as Types from "@aeppic/types";
import { FindOptions, Query } from "../model/index.js";
import { setIntervalNoKeepAlive } from '@aeppic/shared/timer';
import { assert } from "@aeppic/shared/assert";

const isServer =
      typeof window === "undefined" &&
      typeof document === "undefined";
      
export class QueryCache {
  private _queryCache: Map<string, Types.Document[] | Promise<Types.Document[]>>;
  private _formsToCacheQueriesFor: Set<Types.DocumentId>;
  private _cacheHits = 0;
  private _cacheMisses = 0;
  private _uncachedDedups = 0;
  private _activeUncachedQueries = new Map<string, Types.Document[] | Promise<Types.Document[]>>();
  private _allQueries = false;

  constructor(
    // Options to configure the query cache
    {
      forms = [],
      forceClearAfterTimeInMinutes = 0,
      allQueries = false,
    }: { forms?: Types.DocumentId[], forceClearAfterTimeInMinutes?: number, allQueries?: boolean},
    // Function to find data when query not in cache
    private _findUncached: (
      query: Query.Query,
      options: FindOptions,
    ) => Promise<Types.Document[]>,
  ) {
    assert(allQueries
      ? forms == null || forms.length === 0 
      : true, "All queries does not work with forms."
    )

    this._queryCache = new Map<string, Promise<Types.Document[]>>();
    this._formsToCacheQueriesFor = new Set(forms ?? []);
    this._allQueries = allQueries;

    this.scheduleCacheClearing(forceClearAfterTimeInMinutes);
  }

  private scheduleCacheClearing(forceClearAfterTimeInMinutes) {
    if (forceClearAfterTimeInMinutes > 0 && forceClearAfterTimeInMinutes < Number.MAX_SAFE_INTEGER) {
      assert(!isServer, "Clearing the cache on a timer on the server is not supported.")

      const clearCache = () => {
        console.log(`QueryCache: Clearing entire cache after ${forceClearAfterTimeInMinutes} minutes`);
        this._queryCache.clear();
      }
      setIntervalNoKeepAlive(clearCache, forceClearAfterTimeInMinutes * 60 * 1000);
    }
  }

  async find(
    query: Query.Query,
    options: FindOptions,
  ): Promise<Types.Document[]> {
    // debugger
    const queryCacheKey = calculateQueryCacheKey(query, options);

    // If no forms are specified to cache queries for, do not cache anything
    if (this._formsToCacheQueriesFor.size === 0 && !this._allQueries) {
      return this._performUncachedQuery(queryCacheKey, query, options)
    }

    const formIdInQuery = getFormIdFromQuery(query);

    // Only cache queries that query for specific documents of specific forms
    if (this._allQueries || (formIdInQuery && this._formsToCacheQueriesFor.has(formIdInQuery))) {
      return this._performCachedQuery(queryCacheKey, query, options)
    } else {
      return this._performUncachedQuery(queryCacheKey, query, options)
    }
  }

  private async _performCachedQuery(queryCacheKey: string, query: Query.Query,
    options: FindOptions,
  ): Promise<Types.Document[]> {
    const cachedResult = this._queryCache.get(queryCacheKey);

    if (cachedResult != null) {
      this._cacheHits++;
      return await cachedResult;
    }

    try {
      this._cacheMisses++;

      const findRequest = this._findUncached(query, options);
      this._queryCache.set(queryCacheKey, findRequest);

      const documents = await findRequest;
      this._queryCache.set(queryCacheKey, documents);
      return documents;
    } catch (error) {
      this._queryCache.delete(queryCacheKey);
      throw error;
    }
  }

  private async _performUncachedQuery(queryCacheKey: string, query: Query.Query,
    options: FindOptions,
  ): Promise<Types.Document[]> {
    const activeUncachedQuery = this._activeUncachedQueries.get(queryCacheKey);

    if (activeUncachedQuery) {
      this._uncachedDedups++;
      return activeUncachedQuery;
    }

    const findPromise = this._findUncached(query, options);
    this._activeUncachedQueries.set(queryCacheKey, findPromise);

    try {
      const documents = await findPromise;
      return documents;
    } catch (error) {
      throw error;
    } finally {
      this._activeUncachedQueries.delete(queryCacheKey);
    }
  }

  updateCacheForDocument(document: Types.Document) {
    const formId = document.f.id;

    // If the document's form is one we cache queries for, clear the entire cache
    if (this._formsToCacheQueriesFor.has(formId)) {
      console.log(
        `QueryCache: Clearing cache due to update to document of form ${formId}`,
      );
      this._queryCache.clear();
    }
  }
}

export function calculateQueryCacheKey(
  query: Query.Query,
  options: FindOptions = {},
) {
  const cacheKey =
    JSON.stringify(query) + "|" + (options ? JSON.stringify(options) : "");
  return cacheKey;
}

// If the query is a simple AND query, retrieve the form ID being queried for
export function getFormIdFromQuery(query: Query.Query) {
  if (!isAnAndQuery(query)) {
    return;
  }

  return retrieveFormIdRequiredInQuery(query);
}

// Check if the query is an AND query
function isAnAndQuery(query: Query.Query): boolean {
  return (query?.graph as any)?.operator === "AND";
}

// Retrieve the form ID required in the query conditions
function retrieveFormIdRequiredInQuery(query: Query.Query): Types.DocumentId {
  for (const condition of (query.graph as any).conditions) {
    if (condition.field === "f.id") {
      const parameterId = parseInt(condition.term?.id, 10);
      const formId = query.parameters[parameterId];

      if (typeof formId === "string") {
        return formId;
      }
    }
  }
}
